import { useMemo } from "react";

import { excludeEligibility } from "@signalist/shared-types";

import { AssignStatus, InclusionStatus, SelectType } from "@/enum/list.enum";

import { ListPerson } from "../../../../List/schema/list";
import { isDummyProspect } from "../../../utils/columns/dummyProspect";
import { HasItemsPermissions } from "../unassigned-table.types";

interface UseTablePermissionsProps {
	data: ListPerson[];
	selectedItems: number[];
	selectAll: boolean;
	selectType: SelectType;
}

/**
 * Hook for computing permission-based visibility flags for bulk action buttons
 * All hasXxxItems properties determine which bulk actions are available
 */
export const useTablePermissions = ({
	data,
	selectedItems,
	selectAll,
	selectType,
}: UseTablePermissionsProps): HasItemsPermissions => {
	const hasItemsWithMissingEMail = useMemo(() => {
		if (selectAll && selectType === SelectType.ALL) {
			return true;
		}

		return data?.some((item) => {
			if (!selectedItems.includes(item.id)) {
				return false;
			}

			const { mapBusiness, offer, prospect } = item;

			if (mapBusiness) {
				return !mapBusiness.email;
			}

			if (offer) {
				return !offer.email;
			}

			if (prospect) {
				return !prospect.emailStatus;
			}

			return false;
		});
	}, [data, selectAll, selectType, selectedItems]);

	const hasItemsWithMissingPhone = useMemo(() => {
		if (selectAll && selectType === SelectType.ALL) {
			return true;
		}

		return data?.some((item) => {
			if (!selectedItems.includes(item.id)) {
				return false;
			}

			const { prospect } = item;

			if (prospect) {
				return !prospect.phoneStatus;
			}

			return false;
		});
	}, [data, selectAll, selectType, selectedItems]);

	const hasAssignedItems = useMemo(() => {
		const someOfAllAreAssigned = data?.some(
			(item) =>
				item.assignStatus === AssignStatus.RUNNING || item.assignStatus === AssignStatus.ASSIGNED
		);

		const someOfSelectedAreAssigned = data?.some(
			(i) =>
				selectedItems.includes(i.id) &&
				(i.assignStatus === AssignStatus.RUNNING || i.assignStatus === AssignStatus.ASSIGNED)
		);

		return (
			(someOfAllAreAssigned && selectAll && selectType === SelectType.ALL) ||
			someOfSelectedAreAssigned
		);
	}, [data, selectType, selectedItems, selectAll]);

	const hasExcludedItems = useMemo(() => {
		const someOfAllAreExcluded = data?.some((item) =>
			[InclusionStatus.UNSURE, InclusionStatus.NOT_FOUND].includes(item.inclusionStatus)
		);

		const someOfSelectedAreExcluded = data?.some(
			(i) =>
				selectedItems.includes(i.id) &&
				[InclusionStatus.UNSURE, InclusionStatus.NOT_FOUND].includes(i.inclusionStatus)
		);

		return (
			(someOfAllAreExcluded && selectAll && selectType === SelectType.ALL) ||
			someOfSelectedAreExcluded
		);
	}, [data, selectType, selectedItems, selectAll]);

	const hasBlacklistedItems = useMemo(() => {
		const someOfAllAreBlacklisted = data?.some(
			(item) => item.inclusionStatus === InclusionStatus.BLACKLIST
		);

		const someOfSelectedAreBlacklisted = data?.some(
			(i) => selectedItems.includes(i.id) && i.inclusionStatus === InclusionStatus.BLACKLIST
		);

		return (
			(someOfAllAreBlacklisted && selectAll && selectType === SelectType.ALL) ||
			someOfSelectedAreBlacklisted
		);
	}, [data, selectType, selectedItems, selectAll]);

	const hasSnoozableItems = useMemo(() => {
		const someOfAllAreSnoozable = data?.some((item) => item.prospect?.snoozed === false);
		const someOfSelectedAreSnoozable = data?.some(
			(i) => selectedItems.includes(i.id) && i.prospect?.snoozed === false
		);

		return (
			(someOfAllAreSnoozable && selectAll && selectType === SelectType.ALL) ||
			someOfSelectedAreSnoozable
		);
	}, [data, selectType, selectedItems, selectAll]);

	const hasUnsnoozableItems = useMemo(() => {
		const someOfAllAreUnsnoozable = data?.some((item) => item.prospect?.snoozed === true);
		const someOfSelectedAreUnsnoozable = data?.some(
			(i) => selectedItems.includes(i.id) && i.prospect?.snoozed === true
		);

		return (
			(someOfAllAreUnsnoozable && selectAll && selectType === SelectType.ALL) ||
			someOfSelectedAreUnsnoozable
		);
	}, [data, selectType, selectedItems, selectAll]);

	const hasBlacklistableItems = useMemo(() => {
		const someOfAllAreBlacklistable = data?.some(
			(item) => item.inclusionStatus !== InclusionStatus.BLACKLIST
		);

		const someOfSelectedAreBlacklistable = data?.some(
			(i) => selectedItems.includes(i.id) && i.inclusionStatus !== InclusionStatus.BLACKLIST
		);

		return (
			(someOfAllAreBlacklistable && selectAll && selectType === SelectType.ALL) ||
			someOfSelectedAreBlacklistable
		);
	}, [data, selectType, selectedItems, selectAll]);

	const hasUnarchiveableItems = useMemo(() => {
		const someOfAllAreUnarchiveable = data?.some(
			(item) => item.assignStatus === AssignStatus.ARCHIVED
		);

		const someOfSelectedAreUnarchiveable = data?.some(
			(i) => selectedItems.includes(i.id) && i.assignStatus === AssignStatus.ARCHIVED
		);

		return (
			(someOfAllAreUnarchiveable && selectAll && selectType === SelectType.ALL) ||
			someOfSelectedAreUnarchiveable
		);
	}, [data, selectType, selectedItems, selectAll]);

	const hasUnparkableItems = useMemo(() => {
		const someOfAllAreUnparkable = data?.some(
			(item) => item.assignStatus === AssignStatus.SAVED_FOR_LATER
		);

		const someOfSelectedAreUnparkable = data?.some(
			(i) => selectedItems.includes(i.id) && i.assignStatus === AssignStatus.SAVED_FOR_LATER
		);

		return (
			(someOfAllAreUnparkable && selectAll && selectType === SelectType.ALL) ||
			someOfSelectedAreUnparkable
		);
	}, [data, selectType, selectedItems, selectAll]);

	const hasParkableItems = useMemo(() => {
		const someOfAllAreParkable = data?.some(
			(item) =>
				item.assignStatus !== AssignStatus.SAVED_FOR_LATER &&
				item.inclusionStatus !== InclusionStatus.BLACKLIST &&
				item.assignStatus === AssignStatus.DRAFT
		);

		const someOfSelectedAreParkable = data?.some(
			(i) =>
				selectedItems.includes(i.id) &&
				i.assignStatus !== AssignStatus.SAVED_FOR_LATER &&
				i.inclusionStatus !== InclusionStatus.BLACKLIST &&
				i.assignStatus === AssignStatus.DRAFT
		);

		return (
			(someOfAllAreParkable && selectAll && selectType === SelectType.ALL) ||
			someOfSelectedAreParkable
		);
	}, [data, selectType, selectedItems, selectAll]);

	const hasConfirmableItems = useMemo(() => {
		const someOfAllAreConfirmable = data?.some(
			(item) =>
				item.assignStatus === AssignStatus.DRAFT ||
				item.assignStatus === AssignStatus.SAVED_FOR_LATER
		);

		const someOfSelectedAreConfirmable = data?.some(
			(i) =>
				selectedItems.includes(i.id) &&
				(i.assignStatus === AssignStatus.DRAFT || i.assignStatus === AssignStatus.SAVED_FOR_LATER)
		);

		return (
			(someOfAllAreConfirmable && selectAll && selectType === SelectType.ALL) ||
			someOfSelectedAreConfirmable
		);
	}, [data, selectType, selectedItems, selectAll]);

	const hasAssignableItems = useMemo(() => {
		const someOfAllAreAssignable = data?.some(
			(item) =>
				item.assignStatus !== AssignStatus.RUNNING && item.assignStatus !== AssignStatus.REPLIES
		);

		const someOfSelectedAreAssignable = data?.some(
			(i) =>
				selectedItems.includes(i.id) &&
				i.assignStatus !== AssignStatus.RUNNING &&
				i.assignStatus !== AssignStatus.REPLIES
		);

		return (
			(someOfAllAreAssignable && selectAll && selectType === SelectType.ALL) ||
			someOfSelectedAreAssignable
		);
	}, [data, selectType, selectedItems, selectAll]);

	const hasExcludableItems = useMemo(() => {
		const someOfAllAreExcludable = data?.some((item) => excludeEligibility(item).allowed);

		const someOfSelectedAreExcludable = data?.some(
			(i) => selectedItems.includes(i.id) && excludeEligibility(i).allowed
		);

		return (
			(someOfAllAreExcludable && selectAll && selectType === SelectType.ALL) ||
			someOfSelectedAreExcludable
		);
	}, [data, selectType, selectedItems, selectAll]);

	const hasArchivableItems = useMemo(() => {
		const someOfAllAreArchivable = data?.some(
			(item) =>
				item.assignStatus === AssignStatus.DRAFT &&
				[AssignStatus.DRAFT, AssignStatus.SAVED_FOR_LATER].includes(item.assignStatus)
		);

		const someOfSelectedAreArchivable = data?.some(
			(i) =>
				selectedItems.includes(i.id) &&
				i.assignStatus === AssignStatus.DRAFT &&
				[AssignStatus.DRAFT, AssignStatus.SAVED_FOR_LATER].includes(i.assignStatus)
		);

		return (
			(someOfAllAreArchivable && selectAll && selectType === SelectType.ALL) ||
			someOfSelectedAreArchivable
		);
	}, [data, selectType, selectedItems, selectAll]);

	// Placeholder rows (tracker "SELECT" / import "Person not found") that bulk find-person targets.
	// With select-all we can't cheaply prove a placeholder is selected, so short-circuit to true —
	// the backend filters to placeholder rows regardless.
	const hasPlaceholderItems = useMemo(() => {
		if (selectAll && selectType === SelectType.ALL) {
			return true;
		}

		return data?.some((item) => selectedItems.includes(item.id) && isDummyProspect(item));
	}, [data, selectAll, selectType, selectedItems]);

	return {
		hasItemsWithMissingEMail,
		hasItemsWithMissingPhone,
		hasPlaceholderItems,
		hasAssignedItems,
		hasExcludedItems,
		hasBlacklistedItems,
		hasSnoozableItems,
		hasUnsnoozableItems,
		hasBlacklistableItems,
		hasUnarchiveableItems,
		hasUnparkableItems,
		hasParkableItems,
		hasConfirmableItems,
		hasAssignableItems,
		hasExcludableItems,
		hasArchivableItems,
	};
};
